// Pure helpers for the three "stop asking" cleanup escapes
// (Grant-approved 2026-07-14). Each detector's recommended action still
// tries to FIX the record; the rail additionally offers an honest terminal
// answer for when a human has looked and there is genuinely nothing to
// recover. Picking it posts "keep-current", which writes a durable unknown
// marker (migration 0203) so the reviewed record stops re-appearing every
// ~6h sweep. Same escape shape + conventions as marital-issue.ts /
// primary-issue.ts / pm-entity-issue.ts.
//
// Root-safe (imports nothing) so it's unit-tested from tests/web/.

export interface StopAskingIssueLike {
  detectorSlug: string;
}

// ─── contact-no-contact-method ───────────────────────────────────────

export function isNoContactMethodIssue(issue: StopAskingIssueLike): boolean {
  return issue.detectorSlug === "contact-no-contact-method";
}

export const NO_CONTACT_METHOD_KEEP_LABEL =
  "No email or phone — keep record anyway";

export function noContactMethodKeepConsequence(): string {
  return "Records that this contact genuinely has no email or phone on file and should stay in the system anyway — the item resolves for the whole team and it won't be asked again. Adding an email or phone on the record any time supersedes this. Reversible for 60 seconds (undo clears the answer and reopens the item).";
}

// P1-6 (cleanup UX audit, 2026-07-13) — the inline "verify" quick-edit:
// typing an email and/or phone IS the action (matching the client-since
// / expiration doctrine). At least one is required — mirrors the
// backend's contact_method_required 400.
export function addContactMethodConsequence(
  email: string,
  phone: string,
): string {
  const parts: string[] = [];
  if (email.trim()) parts.push(`email (${email.trim()})`);
  if (phone.trim()) parts.push(`phone (${phone.trim()})`);
  if (parts.length === 0) {
    return `Enter an email or a phone number — adding at least one is the whole action here. Genuinely neither? Use “${NO_CONTACT_METHOD_KEEP_LABEL}” below.`;
  }
  return `Adds the ${parts.join(" and ")} to this contact and resolves the issue. Reversible from the contact's History tab.`;
}

// ─── sr-missing-effective-date ───────────────────────────────────────

export function isEffectiveDateUnknownIssue(
  issue: StopAskingIssueLike,
): boolean {
  return issue.detectorSlug === "sr-missing-effective-date";
}

export const EFFECTIVE_DATE_UNKNOWN_LABEL =
  "Start date not recoverable — stop asking";

export function effectiveDateUnknownConsequence(): string {
  return "Records that this record's start (effective) date genuinely can't be recovered — the item resolves for the whole team and it won't be asked again. If the date ever turns up, entering it above supersedes this. Reversible for 60 seconds (undo clears the answer and reopens the item).";
}

// ─── mortgage-missing-funded-date ────────────────────────────────────

export function isFundedDateUnknownIssue(
  issue: StopAskingIssueLike,
): boolean {
  return issue.detectorSlug === "mortgage-missing-funded-date";
}

export const FUNDED_DATE_UNKNOWN_LABEL =
  "Funded date not recoverable — stop asking";

export function fundedDateUnknownConsequence(): string {
  return "Records that this loan's funded date genuinely can't be recovered — the item resolves for the whole team and it won't be asked again. If the date ever turns up, setting it on the loan supersedes this. Reversible for 60 seconds (undo clears the answer and reopens the item).";
}

// ─── realty-pending-transaction-needs-review ─────────────────────────
//
// Nate 2026-07-14: the "has it closed? — set the close date" item had no
// honest answer for a listing/search that ISN'T under contract yet (no
// close date to give). This escape records that, so it stops being asked.

export function isRealtyStillActiveIssue(
  issue: StopAskingIssueLike,
): boolean {
  return issue.detectorSlug === "realty-pending-transaction-needs-review";
}

export const REALTY_STILL_ACTIVE_LABEL =
  "Still an active listing — not under contract yet";

export function realtyStillActiveConsequence(): string {
  return "Records that this is still active and not under contract yet — there is no close date to give — so the item resolves for the whole team and won't be asked again. If it later goes under contract and closes, marking it closed (with the close date) supersedes this. Reversible for 60 seconds (undo clears the answer and reopens the item).";
}
